
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { browserTimezone } from '../../lib/datetime.js'
import { api, type StudentLesson } from '../../lib/api.js'
import { useLocale } from '../../i18n/index.js'
import { Column, Main } from '../../classroom/Shell.js'
import { Bar, ErrorLine, More, RowText, Rows, SectionHead, Skeleton, Status, type Tone } from '../../classroom/ui.js'
import { capitalise } from './StudentHomePage.js'
import { titleFor } from './CoursePage.js'
import { studentCopy } from './copy.js'
import { shellCopy } from './copyShell.js'
import { OurPlan } from './OurPlan.js'

const tone: Record<string, Tone> = { confirmed: 'ok', pending: 'wait', completed: 'neutral', cancelled: 'bad', no_show: 'bad' }

/**
 * Lekcje z Dianą (Student-Lessons.png): the package and what is left of it,
 * the next lesson as the one hero card (join, or cancel up to a day ahead —
 * the terms), „Nasz plan” when Diana keeps one for these classes, then what
 * is coming and what has been.
 */
export function LessonsPage() {
  const locale = useLocale()
  const t = studentCopy(locale)
  const l = shellCopy(locale).lessons
  const queryClient = useQueryClient()
  const { data, isPending, isError } = useQuery({ queryKey: ['student', 'me'], queryFn: api.student.me })
  // Shares its cache with „Nasz plan” below.
  const plans = useQuery({ queryKey: ['course', 'plans'], queryFn: api.course.plans })
  const cancel = useMutation({
    mutationFn: api.student.cancel,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['student', 'me'] }),
  })

  /*
   * The student's own zone, from their device — as on the booking page, where
   * they picked the time. profile.timezone is never set (it is always
   * Europe/Warsaw), so a student in Kyiv who booked 18:00 read „17:00”.
   */
  const tz = browserTimezone()
  const day = (iso: string) => capitalise(DateTime.fromISO(iso).setZone(tz).setLocale(locale).toFormat('cccc, d MMMM'))
  const hours = (x: StudentLesson) =>
    `${DateTime.fromISO(x.startsAt).setZone(tz).toFormat('HH:mm')}–${DateTime.fromISO(x.endsAt).setZone(tz).toFormat('HH:mm')}`

  const upcoming = data?.lessons.upcoming ?? []
  const [next, ...later] = upcoming
  // What the next class covers, when Diana tied a plan lesson to it.
  const covers = next
    ? (plans.data ?? []).flatMap((p) => p.items).find((i) => i.lesson && i.classAt && Date.parse(i.classAt) === Date.parse(next.startsAt))
    : undefined
  const balance = data?.balance ?? null
  const booking = locale === 'pl' ? '/rezerwacja' : `/${locale}/rezerwacja`

  const onCancel = (x: StudentLesson) => {
    if (window.confirm(`${l.cancel}: ${day(x.startsAt)}, ${hours(x)}?`)) cancel.mutate(x.id)
  }

  return (
    <Column
      top={<p className="m-0 font-semibold">{shellCopy(locale).nav.lessons}</p>}
      bottom={
        <>
          <span className="hidden text-class-gloss text-class-ink-soft desk:inline">{l.moreTime}</span>
          <a href={booking} className="ml-auto font-semibold">
            {l.book} ›
          </a>
        </>
      }
    >
      <Main width={720}>
        {isPending ? (
          <Skeleton lines={5} />
        ) : isError || !data ? (
          <ErrorLine>{t.errors.load}</ErrorLine>
        ) : (
          <>
            <section className="flex flex-col gap-2.5">
              <h1 className="cl-title m-0">{shellCopy(locale).nav.lessons}</h1>
              {balance && balance.lessonsBought > 0 ? (
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2.5">
                  <span className="text-class-ink-soft">
                    {l.package(balance.lessonsBought, Math.max(0, balance.lessonsRemaining))}
                    <strong className="font-semibold text-class-ink">{Math.max(0, balance.lessonsRemaining)}</strong>
                  </span>
                  <Bar
                    value={(Math.max(0, balance.lessonsRemaining) / balance.lessonsBought) * 100}
                    className="min-w-40 flex-1 max-desk:basis-full"
                  />
                </div>
              ) : (
                <p className="m-0 text-class-ink-soft">{l.noPackage}</p>
              )}
            </section>

            {next ? (
              <section className="flex flex-col gap-5 rounded-class-lg bg-class-sky-200 p-[22px] desk:p-8">
                <div className="flex flex-col gap-1">
                  <span className="text-class-gloss font-semibold text-class-sky-700">{l.next}</span>
                  <p className="m-0 font-class-display text-class-heading leading-tight">{day(next.startsAt)}</p>
                  <p className="m-0 text-class-ink-soft">
                    {hours(next)} · {l.more} · {l.online}
                    {next.status === 'pending' && ` · ${l.status.pending}`}
                  </p>
                  {covers?.lesson && (
                    <Link to={`/panel/kurs/${covers.course.slug}/${covers.lesson.slug}`} className="mt-1 self-start font-semibold">
                      {shellCopy(locale).plan.onClass}: {shellCopy(locale).course.lesson(covers.lesson.number)} · {titleFor(covers.lesson, locale)} ›
                    </Link>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                  {next.meetingUrl && next.status === 'confirmed' && (
                    <a href={next.meetingUrl} target="_blank" rel="noreferrer" className="cl-btn cl-btn-primary min-h-12 max-desk:w-full">
                      {l.join}
                    </a>
                  )}
                  {next.canCancel ? (
                    <span className="text-class-gloss text-class-ink-soft">
                      <button type="button" onClick={() => onCancel(next)} disabled={cancel.isPending} className="cl-link-quiet">
                        {l.cancel}
                      </button>{' '}
                      · {l.cancelRule}
                    </span>
                  ) : (
                    <span className="text-class-gloss text-class-ink-soft">{l.cancelTooLate}</span>
                  )}
                </div>
                {cancel.isError && <ErrorLine>{t.errors.load}</ErrorLine>}
              </section>
            ) : (
              <section className="rounded-class-lg bg-class-sky-200 p-[22px] desk:p-8">
                <p className="m-0 font-class-display text-class-heading">{l.noLessons}</p>
                <p className="m-0 mt-1 text-class-ink-soft">{l.noLessonsHint}</p>
              </section>
            )}

            <OurPlan locale={locale} timezone={tz} />

            {later.length > 0 && (
              <section className="flex flex-col">
                <SectionHead>{l.upcoming}</SectionHead>
                <Rows>
                  {later.map((x) => (
                    <div key={x.id} className="flex min-h-16 items-center gap-4 px-1 py-2">
                      <RowText title={day(x.startsAt)} meta={hours(x)} />
                      {x.canCancel && (
                        <button type="button" onClick={() => onCancel(x)} className="cl-link-quiet hidden text-class-gloss desk:inline">
                          {l.cancel}
                        </button>
                      )}
                      <Status tone={tone[x.status] ?? 'neutral'} className="!text-class-gloss">
                        {l.status[x.status] ?? x.status}
                      </Status>
                    </div>
                  ))}
                </Rows>
              </section>
            )}

            {data.lessons.past.length > 0 && (
              <section className="flex flex-col">
                <SectionHead>{l.history}</SectionHead>
                <Rows>
                  {data.lessons.past.map((x) => (
                    <div key={x.id} className="flex min-h-16 items-center gap-4 px-1 py-2">
                      <RowText title={day(x.startsAt)} meta={hours(x)} />
                      <Status tone={tone[x.status] ?? 'neutral'} className="!text-class-gloss">
                        {l.status[x.status] ?? x.status}
                      </Status>
                    </div>
                  ))}
                </Rows>
              </section>
            )}

            <More to="/panel/konto" quiet className="self-start desk:hidden">
              {shellCopy(locale).account.purchases}
            </More>
          </>
        )}
      </Main>
    </Column>
  )
}

